import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import './BottomSheet.css';

const SWIPE_CLOSE_PX = 72;
const SWIPE_FLICK_PX = 28;
const SWIPE_FLICK_VELOCITY = 0.55;

function motionReduced() {
  if (document.documentElement.getAttribute('data-display-mode') === 'paperwhite') return true;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function scrolledWithin(target, sheet) {
  let el = target instanceof Element ? target : target?.parentElement;
  while (el && el !== sheet) {
    if (el.scrollTop > 0) return true;
    el = el.parentElement;
  }
  return false;
}

export function BottomSheet({ isOpen, onClose, title, footer, children }) {
  const sheetRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap + auto-focus on open
  useEffect(() => {
    if (!isOpen) return;
    const sheet = sheetRef.current;
    if (!sheet) return;

    const focusableEls = sheet.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    sheet.addEventListener('keydown', handleTab);
    firstEl?.focus();
    return () => sheet.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Swipe down closes. A scroll that has already moved stays a scroll.
  // Mouse clicks are unchanged for the sheets that already use this.
  useEffect(() => {
    if (!isOpen) return undefined;
    const sheet = sheetRef.current;
    if (!sheet) return undefined;
    const drag = {
      active: false,
      pulled: false,
      startY: 0,
      lastY: 0,
      startTime: 0,
    };

    const onStart = (event) => {
      if (event.touches.length !== 1) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('button, a, input, textarea, select, [role="button"]')) return;
      const fromChrome = Boolean(target.closest('.bottom-sheet-drag-handle, .bottom-sheet-header'));
      const inSheet = sheet.contains(target);
      if (!fromChrome && !inSheet) return;
      if (!fromChrome && scrolledWithin(target, sheet)) return;
      drag.active = true;
      drag.pulled = false;
      drag.startY = event.touches[0].clientY;
      drag.lastY = drag.startY;
      drag.startTime = Date.now();
    };

    const onMove = (event) => {
      if (!drag.active || event.touches.length !== 1) return;
      const dy = event.touches[0].clientY - drag.startY;
      drag.lastY = event.touches[0].clientY;
      if (dy <= 0) {
        sheet.style.transform = '';
        return;
      }
      if (dy < 8) return;
      drag.pulled = true;
      sheet.style.transition = 'none';
      sheet.style.animation = 'none';
      if (!motionReduced()) sheet.style.transform = `translateY(${dy}px)`;
      event.preventDefault();
    };

    const finish = (event) => {
      if (!drag.active) return;
      const dy = drag.lastY - drag.startY;
      const elapsed = Math.max(Date.now() - drag.startTime, 1);
      const velocity = dy / elapsed;
      const pulled = drag.pulled;
      const shouldClose = pulled && (
        dy > SWIPE_CLOSE_PX || (dy > SWIPE_FLICK_PX && velocity > SWIPE_FLICK_VELOCITY)
      );
      drag.active = false;
      drag.pulled = false;
      if (pulled && event.cancelable) event.preventDefault();
      if (shouldClose) {
        onCloseRef.current();
        return;
      }
      if (sheet.style.transform) {
        sheet.style.transition = motionReduced() ? 'none' : 'transform 180ms ease-out';
        requestAnimationFrame(() => {
          sheet.style.transform = '';
        });
      }
    };

    sheet.addEventListener('touchstart', onStart, { passive: true });
    sheet.addEventListener('touchmove', onMove, { passive: false });
    sheet.addEventListener('touchend', finish);
    sheet.addEventListener('touchcancel', finish);
    return () => {
      sheet.removeEventListener('touchstart', onStart);
      sheet.removeEventListener('touchmove', onMove);
      sheet.removeEventListener('touchend', finish);
      sheet.removeEventListener('touchcancel', finish);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="bottom-sheet-backdrop" onClick={onClose}>
      <div
        className="bottom-sheet"
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="bottom-sheet-drag-handle" />
        <div className="bottom-sheet-header">
          <h2 className="bottom-sheet-title">{title}</h2>
          <button
            type="button"
            className="bottom-sheet-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="bottom-sheet-content">
          {children}
        </div>
        {footer && (
          <div className="bottom-sheet-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
